"use client";
import { EventSourceInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import React from "react";
import huLocale from "@fullcalendar/core/locales/hu";
import Scroll from "@/components/scroll";
import { Link } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import { defaultEvents } from "@/lib/events";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const EventDate = ({ start, end }: { start: string; end?: string }) => {
  const isText = typeof end === "undefined";
  return (
    <div className="flex">
      {isText ? (
        <div key={start}>{start}</div>
      ) : (
        <div>
          <ul>
            <li key={`${start}-1`}>{start}</li>
            <li key={`${end}-2`}>&mdash; {end}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

const EventItem = ({
  eventKey,
  start,
  end,
  title,
  description,
  url,
  signup,
}: ExtendedEventSourceInput) => {
  return (
    <div className="flex text-black" key={eventKey}>
      <div className="flex-2 flex w-[300px] p-2 text-lg">
        <i className="bi bi-calendar-event mr-2"></i>
        <EventDate start={start} end={end} />
      </div>
      <div className="flex-1 p-2 text-left font-bold">
        <Link to={url}>{title}</Link>
        <div className="text-sm font-normal">{description}</div>
      </div>
      {signup && (
        <div className="my-auto flex-1 p-4 text-left">
          <Link
            to={`${signup}`}
            className="rounded-md bg-red-500 p-4 font-bold text-white"
          >
            Jelentkezés
          </Link>
        </div>
      )}
    </div>
  );
};

type ExtendedEventSourceInput = EventSourceInput & {
  eventKey?: string;
  key?: string;
  start: string;
  end?: string;
  title: string;
  url: string;
  color?: string;
  textColor?: string;
  editable?: boolean;
  durationEditable?: boolean;
  allDay?: boolean;
  display?: string;
  description?: string;
  signup?: string;
};

export default function EventsContent() {
  // Separate events into past (no signup) and current (with signup)
  const pastEvents = defaultEvents.filter(event => !event.hideOnList && !event.signup);
  const currentEvents = defaultEvents.filter(event => !event.hideOnList && event.signup);

  return (
    <>
      <div>
        <Scroll />
      </div>
      <h1 className="mx-auto mb-8 mt-8 flex justify-center text-center text-4xl font-bold">
        Események
      </h1>
      
      <div className="m-4 mx-auto mt-8 w-3/4">
        {/* Past events in accordion */}
        {pastEvents.length > 0 && (
          <Accordion type="single" collapsible className="mb-8">
            <AccordionItem value="past-events">
              <AccordionTrigger className="text-xl font-semibold text-amber-800">
                Korábbi események ({pastEvents.length})
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2">
                  {pastEvents.map((event: any, index: number) => (
                    <EventItem
                      key={`past-event-${index}`}
                      eventKey={`past-event-${index}`}
                      {...event}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Current events with signup */}
        {currentEvents.length > 0 && (
          <>
            <h2 className="mb-6 text-2xl font-bold text-amber-800">
              Aktuális programok
            </h2>
            <div className="space-y-2">
              {currentEvents.map((event: any, index: number) => (
                <EventItem
                  key={`current-event-${index}`}
                  eventKey={`current-event-${index}`}
                  {...event}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="m-4 mx-auto mt-8 w-3/4">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
          initialView="dayGridMonth"
          locale={huLocale}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,listWeek",
          }}
          events={defaultEvents}
        />
      </div>
    </>
  );
}
